// utils/paymentMethod.ts
//
// FEE-RATE-SEMANTICS-1 Phase 2B -- shared, exact payment-method helpers so
// SaleEntry and SaleDetail cannot drift on either cash classification or
// fee-rate interpretation.

export type FeeReadableMethod = {
  type?: string | null;
  name?: string | null;
  // Backend-derived effective percentage (Phase 1 always populates it on reads).
  fee_rate_percent?: number | null;
  fixed_fee?: number | null;
};

/**
 * Exact cash classifier: normalized `type === "cash"`, with the NAME fallback
 * used ONLY when type is absent (and then requiring an exact "cash" name). It
 * deliberately does NOT classify an arbitrary method as cash merely because its
 * name contains the substring "cash".
 */
export function isCashPaymentMethod(pm?: FeeReadableMethod | null): boolean {
  const type = (pm?.type || "").toLowerCase();
  if (type) return type === "cash";
  return (pm?.name || "").toLowerCase() === "cash";
}

/** Effective fee percentage (e.g. 2.9 == 2.9%) from the derived field. */
export function paymentMethodFeePercent(pm?: FeeReadableMethod | null): number {
  const pct = pm?.fee_rate_percent;
  return typeof pct === "number" && Number.isFinite(pct) ? pct : 0;
}

/**
 * Effective fee fraction for NON-authoritative previews (percent / 100, exactly
 * once). Posting a configured fee sends NO computed fee -- the backend is
 * authoritative -- so this is display-only.
 */
export function paymentMethodFeeFraction(pm?: FeeReadableMethod | null): number {
  return paymentMethodFeePercent(pm) / 100;
}

/** Fixed per-transaction fee (major units), 0 when absent. */
export function paymentMethodFixedFee(pm?: FeeReadableMethod | null): number {
  const f = pm?.fixed_fee;
  return typeof f === "number" && Number.isFinite(f) ? f : 0;
}
